import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, Trash2, RefreshCw } from "lucide-react";

const RESOURCES = [
  { key: "clients", label: "Clients" },
  { key: "projects", label: "Projects" },
  { key: "project_status_updates", label: "Project Status Updates" },
  { key: "timesheets", label: "Timesheets" },
  { key: "reimbursements", label: "Reimbursements" },
  { key: "leaves", label: "Leaves" },
  { key: "payroll", label: "Payroll" },
  { key: "holidays", label: "Holidays" },
  { key: "policies", label: "Policies" },
  { key: "contact_messages", label: "Contact Messages (Landing)" },
  { key: "files", label: "Uploaded Files (Receipts/Policies)" },
];

export default function DataManagement() {
  const { user } = useAuth();
  const isAllowed = ["partner", "admin"].includes(user?.role);
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [scope, setScope] = useState("selected"); // 'selected' | 'all'
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/data/stats")
      .then((r) => setStats(r.data))
      .catch((err) => toast.error(formatApiError(err)));

  useEffect(() => {
    if (isAllowed) load();
  }, [isAllowed]);

  if (!isAllowed) {
    return (
      <div className="max-w-2xl" data-testid="data-mgmt-forbidden">
        <h1 className="text-3xl font-sans text-slate-900">Data Management</h1>
        <p className="text-sm text-slate-600 mt-2">Only Partner / Admin can access this page.</p>
      </div>
    );
  }

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const selectAll = () => {
    const next = {};
    RESOURCES.forEach((r) => (next[r.key] = true));
    setSelected(next);
  };
  const clearSelection = () => setSelected({});

  const openConfirm = (mode) => {
    if (mode === "selected" && Object.values(selected).every((v) => !v)) {
      toast.error("Select at least one data type to delete");
      return;
    }
    setScope(mode);
    setConfirmText("");
    setConfirmOpen(true);
  };

  const submit = async () => {
    if (confirmText !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setBusy(true);
    try {
      const resources =
        scope === "all" ? ["all"] : RESOURCES.filter((r) => selected[r.key]).map((r) => r.key);
      const r = await api.post("/admin/data/clear", { resources, confirm: "DELETE" });
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${total} records`);
      setConfirmOpen(false);
      setSelected({});
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const totalSelected = RESOURCES.filter((r) => selected[r.key]).reduce(
    (sum, r) => sum + (stats[r.key] || 0),
    0
  );

  return (
    <div className="space-y-6" data-testid="data-mgmt-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-sans text-slate-900">Data Management</h1>
          <p className="text-sm text-slate-600 mt-1">
            Permanently delete operational data. User accounts are never deleted here — manage them
            from the <span className="font-medium">People</span> module.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-stats-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="border border-amber-200 bg-amber-50 rounded-sm p-4 flex gap-3" data-testid="data-mgmt-warning">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <strong>Warning:</strong> Deletion is permanent and cannot be undone. Uploaded files in
          Object Storage are not removed by this action — only their database references.
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Operational Data</div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={selectAll} data-testid="select-all-btn">
              Select all
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
              Clear
            </Button>
          </div>
        </div>
        <div className="divide-y divide-slate-200">
          {RESOURCES.map((r) => {
            const count = stats[r.key] ?? 0;
            return (
              <label
                key={r.key}
                className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 cursor-pointer"
                data-testid={`resource-row-${r.key}`}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={!!selected[r.key]}
                    onCheckedChange={() => toggle(r.key)}
                    disabled={count === 0}
                    data-testid={`resource-checkbox-${r.key}`}
                  />
                  <span className="text-sm text-slate-900">{r.label}</span>
                </div>
                <span
                  className={`text-xs font-mono ${count === 0 ? "text-slate-400" : "text-slate-700"}`}
                  data-testid={`resource-count-${r.key}`}
                >
                  {count} records
                </span>
              </label>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-600">
            Users in system: <span className="font-mono">{stats.users ?? 0}</span> (not deletable here)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => openConfirm("selected")}
              disabled={busy}
              data-testid="delete-selected-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete selected ({totalSelected})
            </Button>
            <Button
              variant="destructive"
              onClick={() => openConfirm("all")}
              disabled={busy}
              data-testid="delete-all-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Clear ALL data
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" /> Confirm permanent deletion
            </DialogTitle>
            <DialogDescription>
              {scope === "all"
                ? "This will permanently delete ALL operational data across every module. Users will be preserved."
                : `This will permanently delete the selected data types (${
                    RESOURCES.filter((r) => selected[r.key]).map((r) => r.label).join(", ")
                  }).`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-text">
              Type <span className="font-mono font-bold">DELETE</span> to proceed
            </Label>
            <Input
              id="confirm-text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              data-testid="confirm-text-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={busy} data-testid="confirm-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submit}
              disabled={busy || confirmText !== "DELETE"}
              data-testid="confirm-delete-btn"
            >
              {busy ? "Deleting..." : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
